import { Search, X } from "@/components/ui/icons";
import { Pressable, View } from "react-native";

import { Input } from "@/components/ui/Input";
import { slopToFloor } from "@/lib/ui/touchTarget";
import { useThemeColors } from "@/theme/theme";

/**
 * Mirrors the `max_length` the public browse route puts on `q` (`routes_public.browse_event`).
 *
 * The server answers a longer query with a 422, which the storefront renders as a full-page error
 * that also removes this box, so "Try again" would refetch the same query with no way to shorten
 * it. Capping the input keeps the client from building a request the server refuses.
 */
export const BROWSE_QUERY_MAX = 120;

/**
 * The customer browse page's search field (#468).
 *
 * It lives in the screen's header, which is fixed on the Pull Sheet storefront and scrolls with the
 * list on the shop book. The server searches the whole table (#768: name, product type, or the
 * collector number printed on the card), and the screen debounces the query. The clear button
 * appears only when there is text, and the input is capped at `BROWSE_QUERY_MAX`.
 *
 * It is built on `Input` so it keeps the Signal focus ring (DESIGN.md §5) that keyboard users on
 * web need (WCAG 2.4.7). The icons sit over the field with the same `relative` plus absolute
 * pattern `ScanSearchSheet` uses.
 */
export function BrowseSearchBar({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const c = useThemeColors();
  return (
    <View className="relative">
      <View
        aria-hidden
        pointerEvents="none"
        className="absolute left-3 top-0 z-10 h-11 items-center justify-center"
      >
        <Search size={16} color={c.subtle} />
      </View>
      <Input
        testID="browse-search-input"
        accessibilityLabel="Search cards by name or card number"
        placeholder="Name or card number"
        value={value}
        onChangeText={onChange}
        maxLength={BROWSE_QUERY_MAX}
        autoCapitalize="none"
        autoCorrect={false}
        returnKeyType="search"
        // Room for the clear button only while there is text to clear, so the placeholder fits.
        className={value.length > 0 ? "pl-9 pr-11" : "pl-9 pr-3"}
      />
      {value.length > 0
        ? (
          <Pressable
            onPress={() => onChange("")}
            accessibilityRole="button"
            accessibilityLabel="Clear search"
            hitSlop={slopToFloor(44)}
            className="absolute right-0 top-0 h-11 w-11 items-center justify-center"
          >
            <X size={16} color={c.subtle} />
          </Pressable>
        )
        : null}
    </View>
  );
}
